/** Known .fgsecret presets. Runtime may see unknown strings → UI treats as custom. */
export type SecretFilePresetType = 'credentials' | 'card' | 'identity' | 'ssh_key' | 'custom';

export const SECRET_FILE_PRESET_TYPES: readonly SecretFilePresetType[] = [
  'credentials',
  'card',
  'identity',
  'ssh_key',
  'custom',
] as const;

export function isSecretFilePresetType(value: string): value is SecretFilePresetType {
  return (SECRET_FILE_PRESET_TYPES as readonly string[]).includes(value);
}

/** Stable semantic field identity (not the localized label). */
export type SecretFileFieldKey =
  | 'username'
  | 'password'
  | 'website'
  | 'cardholder'
  | 'number'
  | 'brand'
  | 'exp_month'
  | 'exp_year'
  | 'cvv'
  | 'title'
  | 'first_name'
  | 'last_name'
  | 'fiscal_code'
  | 'passport'
  | 'driving_license'
  | 'email'
  | 'phone'
  | 'address1'
  | 'address2'
  | 'address3'
  | 'city'
  | 'state'
  | 'zip'
  | 'country'
  | 'private_key'
  | 'public_key'
  | 'fingerprint'
  | 'notes';

export type SecretFileGroupKey =
  | 'credentials_main'
  | 'credentials_autofill'
  | 'card_main'
  | 'identity_personal'
  | 'identity_ids'
  | 'identity_contact'
  | 'identity_address'
  | 'ssh_main'
  | 'additional_options'
  | 'custom_fields';

/** Widget UI. Unknown runtime strings degrade to text. */
export type FileSecretFieldType =
  | 'text'
  | 'password'
  | 'url'
  | 'username'
  | 'email'
  | 'phone'
  | 'textarea'
  | 'number'
  | 'month'
  | 'year';

export interface SecretFileFieldUi {
  mask?: boolean;
  multiline?: boolean;
  upload?: boolean;
  generator?: boolean;
  options?: string[];
  repeatable?: boolean;
}

export interface SecretFileGroup {
  id: string;
  key?: SecretFileGroupKey | string;
  label: string;
  order: number;
  locked?: boolean;
  /** Opaque per-group bag. FE round-trips; no UI. */
  meta?: Record<string, unknown>;
}

export interface FileSecretField {
  id: string;
  type: FileSecretFieldType | string;
  label: string;
  value: string;
  key?: SecretFileFieldKey | string;
  groupId?: string;
  order?: number;
  locked?: boolean;
  ui?: SecretFileFieldUi;
  /** Opaque per-field bag. FE round-trips; no UI. */
  meta?: Record<string, unknown>;
}

export interface FileSecretData {
  /** Omit / 1 = legacy flat fields. Writers post-feature always write 2. */
  schemaVersion?: number;
  name: string;
  encrypted: boolean;
  hint?: string;
  /** Template used at create; unknown → treat as custom in UI. */
  preset?: SecretFilePresetType | string;
  /** Ordered sections. Membership only via field.groupId (no fieldIds). */
  groups?: SecretFileGroup[];
  /**
   * Decrypted: FileSecretField[].
   * Encrypted: base64 salt|iv|ciphertext — plaintext inside remains JSON array of fields.
   */
  fields: FileSecretField[] | string;
  /** Opaque vendor/app extensions. FE round-trips; no UI. */
  extensions?: Record<string, unknown>;
  /** ISO-8601 last write time (v2 writers). */
  updatedAt?: string;
}

export interface DecryptedFileSecretData {
  schemaVersion: number;
  name: string;
  encrypted: boolean;
  fields: FileSecretField[];
  hint?: string;
  preset: SecretFilePresetType | string;
  groups: SecretFileGroup[];
  extensions?: Record<string, unknown>;
  updatedAt?: string;
}
